import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

// THE INTAKE DESK — where a de-scope request becomes a record before it becomes a run.
//
// Why this screen exists at all: on this deployment the only way a `descope_request`
// row could ever be created was as a side effect of pressing "price this" on the
// review desk. That inverted the actual process. A request arrives from a subgrantee
// by email or portal; the moment it arrives the office is on a statutory response
// clock, whether or not anyone has run the engine yet. Minting the obligation at
// engine time means (a) a request nobody has looked at is invisible, and (b) every
// exploratory run mints a 30-day obligation the office never owed. This box was
// carrying 20 open "requests" that were tuning runs.
//
// So: requests are logged here, first, by a human. The engine is a later step against
// an existing record. This screen also owns the two fields the queue renders but no
// code path ever wrote — assignee and status — which is what makes `withdrawn` a
// reachable state and lets the junk rows be cleared without a psql session.
//
// Everything here degrades rather than lying. If the intake endpoints are not present
// on this build, the register still renders read-only from /descope/reviews and the
// write controls say plainly that they are unavailable.

type PortfolioRow = {
  award_id: string;
  state: string;
  subgrantee: string;
  project_id: string;
  project_code: string;
  project_name: string;
  awarded_bsl_count: number | null;
  grant_amount_usd: string | number | null;
  awarded_locations: string | number;
};

// One row of the register. The two shapes it can arrive in (a request-rooted
// /descope/requests, or the review-rooted /descope/reviews fallback) disagree about
// what `id` means, so the source is tracked alongside and the two ids are resolved
// explicitly rather than guessed per row.
type RegisterRow = {
  id?: string | null;
  request_id?: string | null;
  review_id?: string | null;
  project_id?: string | null;
  project_code?: string | null;
  subgrantee_label?: string | null;
  received_at?: string | null;
  received_via?: string | null;
  requester_name?: string | null;
  stated_reason?: string | null;
  due_by?: string | null;
  days_open?: number | null;
  status?: string | null;
  assigned_to?: string | null;
  assignee_name?: string | null;
  requested_n?: number | null;
  decision?: string | null;
  decided_at?: string | null;
};

type UserRow = { id: string; email: string; name?: string | null; role?: string | null; is_active?: boolean };

type Source = 'requests' | 'reviews';

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const int = (v: unknown) => {
  const n = num(v);
  return n == null ? '—' : n.toLocaleString('en-US');
};
const shortDate = (v: unknown) => {
  if (!v) return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const todayISO = () => new Date().toISOString().slice(0, 10);

/** `404`/`405` from api.ts arrive as an Error whose message starts with the status. */
const isMissingEndpoint = (e: unknown) =>
  /^(404|405)\b/.test(String((e as any)?.message ?? ''));

/** Days a request has been open, preferring the server's own count. */
function daysOpen(r: RegisterRow): number | null {
  if (r.days_open != null) return Math.max(0, Math.round(Number(r.days_open)));
  if (!r.received_at) return null;
  const t = new Date(String(r.received_at)).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

/** Red/amber/green against the response window the request was stamped with. */
function aging(r: RegisterRow): { tone: 'danger' | 'warning' | 'success'; text: string } | null {
  if (!r.due_by) return null;
  const t = new Date(String(r.due_by)).getTime();
  if (Number.isNaN(t)) return null;
  const left = Math.floor((t - Date.now()) / 86_400_000);
  if (left < 0) return { tone: 'danger', text: `${-left}d overdue` };
  if (left <= 7) return { tone: 'warning', text: `due in ${left}d` };
  return { tone: 'success', text: `due in ${left}d` };
}

// A row nobody ever attributed to a person or a reason, still sitting open. On a real
// desk this is either an engine probe or an intake someone abandoned half-done; either
// way it is holding a response clock it should not be holding. Flagged, never
// auto-cleared — withdrawing is a human act and it gets a reason.
function looksUnattributed(r: RegisterRow): boolean {
  const open = String(r.status ?? 'open') === 'open';
  const noWho = !String(r.requester_name ?? '').trim();
  const noWhy = !String(r.stated_reason ?? '').trim();
  return open && noWho && noWhy;
}

export default function GrantorIntakePage() {
  const qc = useQueryClient();

  // ── the register ────────────────────────────────────────────────────────────
  // Prefer the request-rooted list; fall back to the review-rooted one so a box
  // that has not shipped /descope/requests still shows a usable register.
  const register = useQuery<{ source: Source; rows: RegisterRow[] }>({
    queryKey: ['grantor-intake-register'],
    queryFn: async () => {
      try {
        const d = await api.get('/descope/requests');
        const rows = Array.isArray(d) ? d : (d?.items ?? d?.requests ?? d?.rows ?? []);
        return { source: 'requests' as Source, rows: rows as RegisterRow[] };
      } catch (e) {
        if (!isMissingEndpoint(e)) throw e;
        const d = await api.get('/descope/reviews');
        const rows = Array.isArray(d) ? d : (d?.reviews ?? d?.rows ?? []);
        return { source: 'reviews' as Source, rows: rows as RegisterRow[] };
      }
    },
    retry: false,
  });
  const source: Source = register.data?.source ?? 'reviews';
  const rows: RegisterRow[] = register.data?.rows ?? [];
  const registerMissing = register.isError;

  const requestIdOf = (r: RegisterRow): string | null =>
    (source === 'requests' ? (r.id ?? r.request_id) : r.request_id) ?? null;
  const reviewIdOf = (r: RegisterRow): string | null =>
    (source === 'requests' ? r.review_id : (r.id ?? r.review_id)) ?? null;

  const portfolio = useQuery<{ anonymised?: boolean; subgrantees?: PortfolioRow[] }>({
    queryKey: ['descope-portfolio'],
    queryFn: () => api.get('/descope/portfolio'),
  });
  const projects: PortfolioRow[] = portfolio.data?.subgrantees ?? [];

  // Assignee list. /users is admin-only, so a non-admin reviewer gets a read-only
  // assignee column rather than a broken dropdown.
  const users = useQuery<UserRow[]>({
    queryKey: ['users-for-assignment'],
    queryFn: async () => {
      const d = await api.get('/users');
      const items = Array.isArray(d) ? d : (d?.items ?? []);
      return (items as UserRow[]).filter((u) => u.is_active !== false);
    },
    retry: false,
  });
  const canAssign = !users.isError && (users.data?.length ?? 0) > 0;
  const userLabel = (u: UserRow) => u.name?.trim() || u.email;

  // The review-rooted list ships the assignee's NAME but not their id, so a dropdown
  // bound to `assigned_to` alone would render every assigned row as "Unassigned" and
  // invite the reviewer to re-assign work that is already assigned. Resolve the id back
  // from the name when the id is absent; fall back to blank only when neither resolves.
  const assigneeValue = (r: RegisterRow): string => {
    if (r.assigned_to) return r.assigned_to;
    const name = String(r.assignee_name ?? '').trim();
    if (!name) return '';
    const hit = (users.data ?? []).find((u) => userLabel(u) === name || u.email === name);
    return hit?.id ?? '';
  };

  // ── write availability ──────────────────────────────────────────────────────
  // Set the first time a write comes back 404/405, which is the only honest way to
  // learn whether this build carries the intake endpoints.
  const [writesMissing, setWritesMissing] = useState(false);

  // ── the intake form ─────────────────────────────────────────────────────────
  const [projectId, setProjectId] = useState('');
  const [receivedVia, setReceivedVia] = useState('email');
  const [receivedAt, setReceivedAt] = useState(todayISO());
  const [requesterName, setRequesterName] = useState('');
  const [statedReason, setStatedReason] = useState('');
  const [windowDays, setWindowDays] = useState(30);
  const [assignTo, setAssignTo] = useState('');
  const [rawIds, setRawIds] = useState('');
  const [created, setCreated] = useState<{ id: string | null } | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [patchErr, setPatchErr] = useState<string | null>(null);

  const parsedIds = useMemo(
    () => rawIds.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean),
    [rawIds],
  );
  const dueBy = useMemo(() => {
    const t = new Date(receivedAt + 'T12:00:00Z').getTime();
    if (Number.isNaN(t)) return null;
    return new Date(t + windowDays * 86_400_000);
  }, [receivedAt, windowDays]);

  const resetForm = () => {
    setProjectId('');
    setRequesterName('');
    setStatedReason('');
    setRawIds('');
    setAssignTo('');
    setReceivedAt(todayISO());
    setWindowDays(30);
  };

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/descope/requests', body),
    onSuccess: (d: any) => {
      setCreated({ id: d?.id ?? d?.request_id ?? null });
      setFormErr(null);
      resetForm();
      qc.invalidateQueries({ queryKey: ['grantor-intake-register'] });
      qc.invalidateQueries({ queryKey: ['descope-reviews', 'all'] });
    },
    onError: (e: unknown) => {
      if (isMissingEndpoint(e)) setWritesMissing(true);
      setFormErr(String((e as any)?.message ?? e));
    },
  });

  const patch = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/descope/requests/${encodeURIComponent(v.id)}`, v.body),
    onSuccess: () => {
      setPatchErr(null);
      qc.invalidateQueries({ queryKey: ['grantor-intake-register'] });
      qc.invalidateQueries({ queryKey: ['descope-reviews', 'all'] });
    },
    // A rejected assignment or date change must not look like a successful one. The
    // control snaps back on refetch; without this the reviewer would see it snap back
    // and have no idea whether the server refused or the network dropped.
    onError: (e: unknown) => {
      if (isMissingEndpoint(e)) setWritesMissing(true);
      setPatchErr(String((e as any)?.message ?? e));
      qc.invalidateQueries({ queryKey: ['grantor-intake-register'] });
    },
  });

  const submit = () => {
    setCreated(null);
    if (!projectId) return setFormErr('Pick the project the request is against.');
    if (requesterName.trim().length < 2)
      return setFormErr('Record who sent the request — the determination quotes this back.');
    if (statedReason.trim().length < 5)
      return setFormErr(
        'Record the reason the subgrantee states. If the request gave none, say so explicitly.',
      );
    if (!receivedAt) return setFormErr('Record the date the request arrived — the clock runs from it.');
    setFormErr(null);
    create.mutate({
      project_id: projectId,
      received_via: receivedVia,
      received_at: receivedAt,
      requester_name: requesterName.trim(),
      stated_reason: statedReason.trim(),
      due_by: dueBy ? dueBy.toISOString() : null,
      response_window_days: windowDays,
      assigned_to: assignTo || null,
      raw_id_list: parsedIds.length ? parsedIds : null,
      status: 'open',
    });
  };

  // ── register view state ─────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<'open' | 'decided' | 'withdrawn' | 'all'>('open');
  const [onlyUnattributed, setOnlyUnattributed] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkReason, setBulkReason] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState<string | null>(null);

  const shown = useMemo(() => {
    const f = rows
      .filter((r) => statusFilter === 'all' || String(r.status ?? 'open') === statusFilter)
      .filter((r) => !onlyUnattributed || looksUnattributed(r));
    return f.sort((a, b) => (daysOpen(b) ?? 0) - (daysOpen(a) ?? 0));
  }, [rows, statusFilter, onlyUnattributed]);

  const stats = useMemo(() => {
    const open = rows.filter((r) => String(r.status ?? 'open') === 'open');
    return {
      open: open.length,
      overdue: open.filter((r) => aging(r)?.tone === 'danger').length,
      unassigned: open.filter((r) => !r.assigned_to && !r.assignee_name).length,
      unattributed: rows.filter(looksUnattributed).length,
    };
  }, [rows]);

  // Does the list we got actually carry requests that were never priced? This is the
  // observable test of whether the register is rooted in the request record or in the
  // review record, and the banner says whichever is true rather than assuming.
  const unpricedVisible = useMemo(
    () => rows.some((r) => !reviewIdOf(r)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, source],
  );

  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
    [selected],
  );

  const withdrawSelected = async () => {
    if (!selectedIds.length) return;
    if (bulkReason.trim().length < 5) {
      setBulkNote('Give a reason — a withdrawal is a record, not a delete.');
      return;
    }
    setBulkBusy(true);
    setBulkNote(null);
    let ok = 0;
    let failed = 0;
    let lastErr = '';
    for (const id of selectedIds) {
      try {
        const row = rows.find((r) => requestIdOf(r) === id);
        // Never overwrite the subgrantee's own words. `stated_reason` is only filled in
        // when the row never had one — which is exactly the engine-probe case — so a
        // withdrawn real request keeps the reason it arrived with.
        const hadReason = !!String(row?.stated_reason ?? '').trim();
        await api.patch(`/descope/requests/${encodeURIComponent(id)}`, {
          status: 'withdrawn',
          withdrawn_reason: bulkReason.trim(),
          ...(hadReason ? {} : { stated_reason: `Withdrawn at intake: ${bulkReason.trim()}` }),
        });
        ok += 1;
      } catch (e) {
        failed += 1;
        lastErr = String((e as any)?.message ?? e);
        if (isMissingEndpoint(e)) {
          setWritesMissing(true);
          break;
        }
      }
    }
    setBulkBusy(false);
    setSelected({});
    setBulkNote(
      failed ? `Withdrew ${ok}, failed ${failed}${lastErr ? ` — ${lastErr}` : ''}.` : `Withdrew ${ok} request(s).`,
    );
    qc.invalidateQueries({ queryKey: ['grantor-intake-register'] });
    qc.invalidateQueries({ queryKey: ['descope-reviews', 'all'] });
  };

  const muted: CSSProperties = { color: 'var(--text-muted)' };
  const cellNum: CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const label: CSSProperties = {
    fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)',
    marginBottom: 4,
  };
  const input: CSSProperties = { width: '100%', padding: 8, boxSizing: 'border-box' };
  const writeDisabled = writesMissing;

  return (
    <div style={{ maxWidth: 1180 }}>
      <div className="page__header">
        <div>
          <h1 className="page__title">Request Intake</h1>
          <div className="page__subtitle">
            A de-scope request is logged here when it arrives, before anything is priced. The
            response clock starts from the date on the request, not from the day someone got
            around to running the engine.
          </div>
        </div>
        <Link className="btn" to="/grantor">Oversight Queue →</Link>
      </div>

      {writeDisabled ? (
        <div className="map-banner map-banner--error" style={{ marginBottom: 16 }}>
          The intake endpoints (<code>POST /descope/requests</code>,{' '}
          <code>PATCH /descope/requests/:id</code>) answered 404 on this deployment, so logging a
          request and editing assignee/status are unavailable here. The register below is still
          live and read-only.
        </div>
      ) : source === 'reviews' ? (
        <div className="map-banner map-banner--info" style={{ marginBottom: 16 }}>
          <code>GET /descope/requests</code> is not present on this build, so the register below is
          assembled from <code>/descope/reviews</code>.{' '}
          {unpricedVisible
            ? 'That list is request-rooted here — requests logged but never priced are included, and show as "not priced".'
            : 'Every row in that list carries a review, so a request logged but never priced would not appear at all. Log one and it will be missing from this table until the review list is rooted in the request record.'}
        </div>
      ) : null}

      <div className="stat-grid" style={{ marginBottom: 18 }}>
        <div className={'stat-card ' + (stats.open ? 'stat-card--warning' : 'stat-card--success')}>
          <div className="stat-card__label">Open requests</div>
          <div className="stat-card__value">{registerMissing ? '—' : int(stats.open)}</div>
          <div className="stat-card__delta">each one is holding a response clock</div>
        </div>
        <div className={'stat-card ' + (stats.overdue ? 'stat-card--danger' : 'stat-card--success')}>
          <div className="stat-card__label">Past due</div>
          <div className="stat-card__value">{registerMissing ? '—' : int(stats.overdue)}</div>
          <div className="stat-card__delta">past the window stamped at intake</div>
        </div>
        <div className="stat-card stat-card--info">
          <div className="stat-card__label">Unassigned</div>
          <div className="stat-card__value">{registerMissing ? '—' : int(stats.unassigned)}</div>
          <div className="stat-card__delta">open with no reviewer named</div>
        </div>
        <div className="stat-card stat-card--danger">
          <div className="stat-card__label">No requester or reason</div>
          <div className="stat-card__value">{registerMissing ? '—' : int(stats.unattributed)}</div>
          <div className="stat-card__delta">likely engine probes — withdraw, don't delete</div>
        </div>
      </div>

      {/* ---------- Log a request ---------- */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card__header">
          <span>Log a request</span>
          <span style={{ ...muted, fontSize: 12, fontWeight: 400 }}>
            creates the record and starts the clock — no engine run
          </span>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
            <div>
              <div style={label}>Project</div>
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={input}
                      disabled={writeDisabled}>
                <option value="">Select a project…</option>
                {projects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.subgrantee} — {p.project_code} ({int(p.awarded_locations)} awarded)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div style={label}>Received via</div>
              <select value={receivedVia} onChange={(e) => setReceivedVia(e.target.value)} style={input}
                      disabled={writeDisabled}>
                <option value="email">Email</option>
                <option value="portal">Portal submission</option>
                <option value="letter">Letter</option>
                <option value="meeting">Meeting / call</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <div style={label}>Date received</div>
              <input type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)}
                     style={input} disabled={writeDisabled} />
            </div>
            <div>
              <div style={label}>Response window (days)</div>
              <input type="number" min={1} max={365} value={windowDays}
                     onChange={(e) => setWindowDays(Math.max(1, Number(e.target.value) || 30))}
                     style={input} disabled={writeDisabled} />
            </div>
          </div>

          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1.4fr 1fr', marginTop: 12 }}>
            <div>
              <div style={label}>Requested by</div>
              <input value={requesterName} onChange={(e) => setRequesterName(e.target.value)}
                     placeholder="Name on the subgrantee's request" style={input}
                     disabled={writeDisabled} />
            </div>
            <div>
              <div style={label}>Assign to</div>
              <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} style={input}
                      disabled={writeDisabled || !canAssign}>
                <option value="">Unassigned</option>
                {(users.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>{userLabel(u)}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={label}>Reason the subgrantee states</div>
            <textarea value={statedReason} onChange={(e) => setStatedReason(e.target.value)} rows={2}
                      placeholder="In their words. This is quoted back in the determination — if the request stated no reason, write that."
                      style={{ ...input, fontFamily: 'inherit' }} disabled={writeDisabled} />
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={label}>
              BSL / FCC location numbers claimed (optional — {int(parsedIds.length)} parsed)
            </div>
            <textarea value={rawIds} onChange={(e) => setRawIds(e.target.value)} rows={3}
                      placeholder="Paste the list exactly as it arrived. Reconciliation and pricing happen later, on the review desk."
                      style={{ ...input, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                      disabled={writeDisabled} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
            <button className="btn btn--primary" onClick={submit}
                    disabled={writeDisabled || create.isPending}>
              {create.isPending ? 'Logging…' : 'Log request'}
            </button>
            <span style={{ ...muted, fontSize: 12 }}>
              Response due {dueBy ? shortDate(dueBy.toISOString()) : '—'}
            </span>
            {formErr && !writeDisabled && (
              <span className="pill pill--danger" style={{ whiteSpace: 'normal' }}>{formErr}</span>
            )}
          </div>

          {created && (
            <div className="map-banner map-banner--success" style={{ marginTop: 12 }}>
              Request logged{created.id ? ` (${created.id})` : ''}. It is now on the queue and
              aging. When you are ready to price it, open the{' '}
              <Link to={created.id ? `/descope?request=${encodeURIComponent(created.id)}` : '/descope'}>
                De-Scope Review
              </Link>{' '}
              desk.
            </div>
          )}
        </div>
      </div>

      {/* ---------- The register ---------- */}
      <div className="card">
        <div className="card__header">
          <span>Request register</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 400, fontSize: 12 }}>
            {(['open', 'decided', 'withdrawn', 'all'] as const).map((s) => (
              <button key={s} className={'btn' + (statusFilter === s ? ' btn--primary' : '')}
                      style={{ padding: '2px 10px', fontSize: 12 }}
                      onClick={() => { setStatusFilter(s); setSelected({}); }}>
                {s}
              </button>
            ))}
            <label style={{ ...muted, display: 'flex', alignItems: 'center', gap: 5 }}>
              <input type="checkbox" checked={onlyUnattributed}
                     onChange={(e) => { setOnlyUnattributed(e.target.checked); setSelected({}); }} />
              no requester / reason only
            </label>
          </span>
        </div>

        {selectedIds.length > 0 && (
          <div style={{ padding: '10px 16px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13 }}><b>{selectedIds.length}</b> selected to withdraw</span>
            <input value={bulkReason} onChange={(e) => setBulkReason(e.target.value)}
                   placeholder="Why are these being withdrawn? (kept on the record)"
                   style={{ flex: 1, minWidth: 260, padding: 6 }} disabled={writeDisabled} />
            <button className="btn" onClick={withdrawSelected} disabled={writeDisabled || bulkBusy}>
              {bulkBusy ? 'Withdrawing…' : 'Withdraw selected'}
            </button>
          </div>
        )}
        {bulkNote && (
          <div style={{ padding: '0 16px 10px', fontSize: 12, ...muted }}>{bulkNote}</div>
        )}
        {patchErr && !writeDisabled && (
          <div style={{ padding: '0 16px 10px', fontSize: 12 }}>
            <span className="pill pill--danger">Change refused: {patchErr}</span>
          </div>
        )}

        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 28 }} />
              <th>Subgrantee</th>
              <th>Project</th>
              <th>Received</th>
              <th>From</th>
              <th style={cellNum}>Claimed</th>
              <th style={cellNum}>Days open</th>
              <th>Response due</th>
              <th>Assigned to</th>
              <th>Status</th>
              <th>Review</th>
            </tr>
          </thead>
          <tbody>
            {register.isLoading && <tr><td colSpan={11} style={muted}>Loading register…</td></tr>}
            {registerMissing && (
              <tr><td colSpan={11} style={muted}>
                The request register is not answering on this deployment.
              </td></tr>
            )}
            {!register.isLoading && !registerMissing && !shown.length && (
              <tr><td colSpan={11} style={muted}>
                No requests match this filter.
              </td></tr>
            )}
            {shown.map((r, i) => {
              const rid = requestIdOf(r);
              const vid = reviewIdOf(r);
              const age = aging(r);
              const d = daysOpen(r);
              const key = rid ?? vid ?? `row-${i}`;
              const editable = !!rid && !writeDisabled;
              return (
                <tr key={key} style={looksUnattributed(r) ? { opacity: 0.85 } : undefined}>
                  <td>
                    <input type="checkbox" disabled={!editable}
                           checked={!!(rid && selected[rid])}
                           onChange={(e) =>
                             rid && setSelected((p) => ({ ...p, [rid]: e.target.checked }))}
                           title={rid ? 'Select to withdraw (a reason is required)' : 'No request record on this row'} />
                  </td>
                  <td style={{ fontWeight: 600 }}>{r.subgrantee_label ?? '—'}</td>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                    {r.project_code ?? r.project_id ?? '—'}
                  </td>
                  <td>
                    {shortDate(r.received_at)}
                    {r.received_via && (
                      <span style={{ ...muted, fontSize: 11, display: 'block' }}>via {r.received_via}</span>
                    )}
                  </td>
                  <td style={r.requester_name ? undefined : muted}>
                    {r.requester_name ?? 'not recorded'}
                  </td>
                  <td style={cellNum}>{int(r.requested_n)}</td>
                  <td style={cellNum}>{d == null ? '—' : d}</td>
                  <td>
                    {editable ? (
                      <input
                        type="date"
                        value={r.due_by ? String(r.due_by).slice(0, 10) : ''}
                        onChange={(e) =>
                          e.target.value &&
                          patch.mutate({
                            id: rid!,
                            body: { due_by: new Date(e.target.value + 'T12:00:00Z').toISOString() },
                          })}
                        style={{ padding: 3, fontSize: 12 }}
                        title="Extending the response window is a change to the clock this office is held to. It is recorded on the request."
                      />
                    ) : (
                      shortDate(r.due_by)
                    )}{' '}
                    {age && <span className={'pill pill--' + age.tone} style={{ marginLeft: 6 }}>{age.text}</span>}
                  </td>
                  <td>
                    {editable && canAssign ? (
                      <select
                        value={assigneeValue(r)}
                        onChange={(e) =>
                          patch.mutate({ id: rid!, body: { assigned_to: e.target.value || null } })}
                        style={{ padding: 4, maxWidth: 160 }}
                      >
                        <option value="">Unassigned</option>
                        {(users.data ?? []).map((u) => (
                          <option key={u.id} value={u.id}>{userLabel(u)}</option>
                        ))}
                      </select>
                    ) : (
                      <span style={r.assignee_name ? undefined : muted}>
                        {r.assignee_name ?? 'Unassigned'}
                      </span>
                    )}
                  </td>
                  {/* Status is not a free dropdown. `decided` is written by the decision
                      path and nothing else — hand-marking a request decided would put a
                      federal record in a state no determination backs. The one transition
                      this desk owns is withdrawal, and it goes through the reason box
                      above so it lands on the record with a reason attached. */}
                  <td>
                    <span
                      className={
                        'pill pill--' +
                        (String(r.status) === 'withdrawn' ? 'danger'
                          : String(r.status) === 'decided' ? 'success' : 'info')
                      }
                    >
                      {r.status ?? 'open'}
                    </span>
                  </td>
                  <td>
                    {vid ? (
                      <Link to={`/descope?review=${encodeURIComponent(vid)}`}>open</Link>
                    ) : (
                      <span style={muted}>not priced</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ ...muted, fontSize: 12, marginTop: 14, maxWidth: 860 }}>
        Withdrawing closes a request's response clock and leaves the row in the register with the
        reason attached; nothing is deleted. Status is not editable by hand —{' '}
        <em>decided</em> is written only by a determination, so a request can never show as
        decided without one behind it. A request that was never priced shows "not priced";
        that is a real state, not a missing row.
      </p>
    </div>
  );
}
